import { DirhamSymbol } from "dirham-symbol";
/**
 * Updated helper to support the library component
 */

const getDisplayPrefix = (
  showCode: boolean,
  showSymbol: boolean,
  code: string,
  extractedSymbol: string,
): React.ReactNode => {
  if (showCode) return code;
  if (showSymbol) return extractedSymbol;
  return "";
};

export const gxCurrencyFormat = (
  amount?: number | null,
  currencyCode?: string | null,
  locale?: string,
  options?: {
    showSymbol?: boolean;
    showCode?: boolean;
  },
): {
  formatted: React.ReactNode;
  integer: string;
  decimal: string;
  symbol: string | React.ReactNode;
  code: string;
} => {
  if (amount == null || Number.isNaN(amount)) {
    return {
      formatted: "---",
      integer: "---",
      decimal: "",
      symbol: "",
      code: "",
    };
  }

  const showSymbol = options?.showSymbol ?? true;
  const showCode = options?.showCode ?? false;
  const browserLang =
    typeof navigator === "undefined" ? "en-US" : navigator.language;
  const resolvedLocale = locale || browserLang;
  const code = currencyCode?.toUpperCase() || "USD";

  try {
    const currencyFormatter = new Intl.NumberFormat(resolvedLocale, {
      style: "currency",
      currency: code,
    });

    const currencyOptions = currencyFormatter.resolvedOptions();

    const numberFormatter = new Intl.NumberFormat(resolvedLocale, {
      style: "decimal",
      minimumFractionDigits: currencyOptions.minimumFractionDigits,
      maximumFractionDigits: currencyOptions.maximumFractionDigits,
    });

    const formattedNumber = numberFormatter.format(amount);

    let symbol: React.ReactNode = "";
    let displayPrefix: React.ReactNode = "";

    if (code === "AED") {
      symbol = <DirhamSymbol />;

      if (showCode) {
        displayPrefix = code;
      } else if (showSymbol) {
        displayPrefix = <DirhamSymbol />;
      }
    } else {
      const parts = currencyFormatter.formatToParts(amount);
      const extractedSymbol =
        parts.find((p) => p.type === "currency")?.value || "";

      symbol = extractedSymbol;
      displayPrefix = getDisplayPrefix(
        showCode,
        showSymbol,
        code,
        extractedSymbol,
      );
    }

    const formatted = displayPrefix ? (
      <>
        {displayPrefix}
        {"\u00A0"}
        {formattedNumber}
      </>
    ) : (
      formattedNumber
    );

    const decimalSeparator = (1.1)
      .toLocaleString(resolvedLocale)
      .substring(1, 2);
    const numParts = formattedNumber.split(decimalSeparator);
    const integerPart = numParts[0];
    let decimalPart = "";

    const minFractionDigits = currencyOptions.minimumFractionDigits ?? 0;
    const maxFractionDigits = currencyOptions.maximumFractionDigits ?? 2;

    if (numParts[1]) {
      decimalPart = `.${numParts[1]}`;
    } else if (minFractionDigits > 0) {
      decimalPart = "." + "0".repeat(maxFractionDigits);
    }

    return {
      formatted,
      integer: integerPart,
      decimal: decimalPart,
      symbol,
      code,
    };
  } catch (err) {
    console.error("Error in gxCurrencyFormat::", err);
    return {
      formatted: `${code} ${amount.toFixed(2)}`,
      integer: amount.toFixed(2).split(".")[0],
      decimal: `.${amount.toFixed(2).split(".")[1]}`,
      symbol: "",
      code,
    };
  }
};
